import React, {useState, useEffect, useCallback} from 'react';
import SMCModal from '../../components/core/SMCModal';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCLabel from '../../components/Simple/SMCLabel';
import SMCTextInput from '../../components/Simple/SMCTextInput';
import SMCButton from '../../components/Simple/SMCButton';
import {PortalSettingsGetSchema} from '../../api/DBSchema/PortalSettings';
import SMCFileInput from '../../components/Simple/SMCFileInput';
import {produce} from 'immer';
import {setProperty, dateToString} from '../../utils/utils';
import Api from '../../api/SMPFetchHelper';
import {useSMCErrorHandler} from '../../utils/errorUtils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

const certificatesSchema = PortalSettingsGetSchema.pages.certificates.dataListSchema;

function ExternalCaEditorModal({
    isOpen = false,
    onRequestClose,
    onSuccess,
    defaultObject
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const [externalCaObject, setExternalCaObject] = useState({});
    const [isNew, setIsNew] = useState(true);
    
    const name           = externalCaObject[certificatesSchema.name];
    const dn             = externalCaObject[certificatesSchema.dn];
    const expirationDate = externalCaObject[certificatesSchema.expirationDate];
    const fingerprint    = externalCaObject[certificatesSchema.fingerprint];

    const setPageValue = useCallback((param, newValue) => {
        setExternalCaObject(obj => produce(obj, draft => (
            setProperty(draft, param, newValue)
        )))
    }, []);

    const onFinish = useCallback(() => {
        const certificateFile = document.getElementById('fileInput')?.files?.[0];
        if (!certificateFile || !name) {
            return;
        }
        Api.createCaCertificate(name, certificateFile, dn, !!isNew)
        .then(resp => {
            onSuccess && onSuccess(resp.data.data);
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            alert(errorMessage);
        })
    }, [dn, isNew, name, onSuccess, getErrorMessage]);

    // On each new open, reset values
    useEffect(() => {
        if (isOpen) {
            if (defaultObject) {
                setExternalCaObject(defaultObject);
                setIsNew(false);
            }
            else {
                setExternalCaObject({
                    [certificatesSchema.name]: '',
                    [certificatesSchema.dn]: '',
                    [certificatesSchema.expirationDate]: '',
                    [certificatesSchema.fingerprint]: ''
                });
                setIsNew(true);
            }
        }
    }, [isOpen, defaultObject]);

    return (
        <SMCModal
            isOpen={isOpen}
            width={610}
            height={480}
            title={t('SMC_SETTINGS_CERT_EXT_NAME')}
            onRequestClose={onRequestClose}
        >
            <SMCModalBody>
                <SMCLabel bold>{t('SMC_SETTINGS_CERT_EXT_NAME')}</SMCLabel>
                <SMCTextInput
                    value={name}
                    readOnly={!isNew}
                    onChange={e => setPageValue(certificatesSchema.name, e.target.value)}
                    label={t('SMC_SETTINGS_CERT_CA_NAME')}
                />
                <SMCFileInput
                    label={t('SMC_SETTINGS_CERT_LOADFILE_FILE')}
                    id={'fileInput'}
                />
                <SMCTextInput
                    value={dn}
                    label={t('SMC_SETTINGS_CERT_DISTINGUISH_NAME')}
                    readOnly
                />
                <SMCTextInput
                    value={dateToString(expirationDate)}
                    label={t('SMC_SETTINGS_CERT_ROOT_WILLEXPIRE')}
                    readOnly
                />
                <SMCTextInput
                    value={fingerprint}
                    label={t('SMC_SETTINGS_CERT_ROOT_FINGERPRINT')}
                    readOnly
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={onFinish}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_FINISH')}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>

    );
}

export default ExternalCaEditorModal;